import { Command } from 'cmdk';
import { observer } from 'mobx-react-lite';
import {
  useEffect,
  forwardRef,
  createContext,
  type ComponentPropsWithoutRef,
  type FC,
} from 'react';

import { editorStore } from '../utils/editor.store';

import type { Range } from '@tiptap/core';
import type tunnel from 'tunnel-rat';

// eslint-disable-next-line react-refresh/only-export-components
export const EditorCommandTunnelContext = createContext(
  {} as ReturnType<typeof tunnel>
);

interface EditorCommandOutProps {
  readonly query: string;
  readonly range: Range;
}

export const EditorCommandOut: FC<EditorCommandOutProps> = observer(
  ({ query, range }) => {
    useEffect(() => {
      editorStore.setQuery(query);
    }, [query]);

    useEffect(() => {
      editorStore.setRange(range);
    }, [range]);

    useEffect(() => {
      const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter'];
      const onKeyDown = (e: KeyboardEvent) => {
        if (navigationKeys.includes(e.key)) {
          e.preventDefault();
          const commandRef = document.querySelector('#slash-command');

          if (commandRef)
            commandRef.dispatchEvent(
              new KeyboardEvent('keydown', {
                key: e.key,
                cancelable: true,
                bubbles: true,
              })
            );

          return false;
        }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('keydown', onKeyDown);
      };
    }, []);

    return (
      <EditorCommandTunnelContext.Consumer>
        {(tunnelInstance) => <tunnelInstance.Out />}
      </EditorCommandTunnelContext.Consumer>
    );
  }
);

export const EditorCommand = observer(
  forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Command>>(
    ({ children, className, ...rest }, ref) => {
      return (
        <EditorCommandTunnelContext.Consumer>
          {(tunnelInstance) => (
            <tunnelInstance.In>
              <Command
                ref={ref}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                id="slash-command"
                className={className}
                {...rest}
              >
                <Command.Input
                  value={editorStore.query}
                  onValueChange={(v) => editorStore.setQuery(v)}
                  style={{ display: 'none' }}
                />
                {children}
              </Command>
            </tunnelInstance.In>
          )}
        </EditorCommandTunnelContext.Consumer>
      );
    }
  )
);
export const EditorCommandList = Command.List;

EditorCommand.displayName = 'EditorCommand';
